import moment from 'moment';
import { getLucideIconByName, createIconElement } from '@packages/ui-components';

export default class Pannellum {
  private w: any;

  public hotspotsMap: any;

  public index: number;

  public active: boolean;

  public loaded = false;

  static AWS_PATH: any;

  viewer: any;

  onLoaded: any;

  showLoading: any;

  id: string;

  nextId: string;

  firstScene: string;

  autoLoad: boolean;

  status = 0; // 0 - init, 1 - loading, 2 - done

  loadCurrentScenes: any;

  hd: boolean;

  updatePanoRotation: any;

  openHotspot: any;

  loadSelf: any;

  transition: any;

  AWS_PATH: string;

  constructor(props: any) {
    this.onLoaded = props.onLoaded;
    this.showLoading = props.showLoading;
    this.autoLoad = props.autoLoad;
    this.hd = props.hd;
    this.id = `panorama-player-${props.id}`;
    this.index = props.id - 1;
    this.firstScene = props.firstScene;
    this.hotspotsMap = {};
    this.loadCurrentScenes = props.loadCurrentScenes;
    this.updatePanoRotation = props.updatePanoRotation;
    this.openHotspot = props.openHotspot;
    this.active = false;
    this.loadSelf = props.loadSelf;
    this.transition = props.transition;
    this.AWS_PATH = props.AWS_PATH;
    this.init();
  }

  private init() {
    if (this.status === 1 || this.status === 2) return;
    this.status = 1;
    if (window) {
      this.w = window;
      this.viewer = this.w.pannellum.viewer(this.id, {
        default: {
          firstScene: this.firstScene,
        },
        autoLoad: this.autoLoad,
        showControls: false,
        scenes: [],
        compass: true,
        crossOrigin: 'anonymous',
      });
      this.status = 2;
      this.viewer.on('animatefinished', (pos: any) => {
        if (this.active) {
          this.updatePanoRotation(this.getNorthOffset(), pos, this.viewer.getScene());
        }
      });
      this.viewer.on('load', () => {
        this.loaded = true;
        if (this.active) {
          this.onLoaded(this.id);
        }
      });
      this.viewer.on('error', (e) => {
        console.error('panorama error:', e);
        this.onLoaded(this.id);
      });
    }
  }

  public addScene(scene: any) {
    const config = this.getMultires(scene);
    if (scene.hotspots) {
      config.hotSpots = [];
      for (let i = 0; i < scene.hotspots.length; i += 1) {
        const h = { sceneUrl: scene.url, ...scene.hotspots[i] };
        config.hotSpots.push(this.generateHotspot(h, scene));
      }
    }
    this.viewer.addScene(scene.name, config);
  }

  private generateHotspot(hotspot: any, scene: any) {
    hotspot.id = `h-${hotspot.id}`;
    hotspot.title = hotspot.name;
    hotspot.createTooltipFunc = Pannellum.addDivs(scene);
    hotspot.createTooltipArgs = {
      text: hotspot.name,
      animated: hotspot.type === 'panorama',
      iconFile: hotspot.iconFile,
      iconColor: hotspot.iconColor,
      iconName: hotspot.icon,
    };

    if (hotspot.iconFile?.length) {
      hotspot.cssClass = 'pano-hotspot pnlm-tooltip pnlm-custom-icon';
    } else {
      hotspot.cssClass = 'pano-hotspot pnlm-tooltip';
    }

    if (hotspot.type === 'image') {
      hotspot.url = `${this.AWS_PATH}/tours/${scene.tourId}/panoramas/${hotspot.sceneUrl}/${hotspot.url}`;
      hotspot.urlMobile = `${this.AWS_PATH}/tours/${scene.tourId}/mobile/panoramas/${hotspot.sceneUrl}/${hotspot.urlMobile}`;
    }

    if (hotspot.type === 'panorama') {
      if (!hotspot.useIcon) {
        hotspot.cssClass = 'pano-hotspot pnlm-tooltip animated pano-arrows';
      }

      hotspot.clickHandlerFunc = () => {
        if (!this.transition) {
          this.loadSelf(hotspot.scene);
        } else {
          this.showLoading(true);
          const nextPannellum = this.hotspotsMap[hotspot.id];
          if (!nextPannellum.isLoaded()) return;
          this.zoomIn(hotspot.pitch, hotspot.yaw);
          setTimeout(() => {
            this.show(nextPannellum.id);
          }, 200);
          setTimeout(() => {
            this.loadCurrentScenes(hotspot.scene, nextPannellum.index);
          }, 550);
        }
      };
    } else if (hotspot.type === 'tab') {
      hotspot.URL = hotspot.url;
    } else if (hotspot.type !== 'text') {
      hotspot.clickHandlerFunc = () => {
        this.openHotspot(hotspot);
      };
    }
    return hotspot;
  }

  private static addDivs = (scene: any) => (hotSpotDiv: any, args: any) => {
    if (args.text) {
      const span = document.createElement('span');
      span.innerHTML = args.text;
      hotSpotDiv.appendChild(span);
      span.style.width = `${span.scrollWidth + 10}px`;
      span.style.marginLeft = `${-(span.scrollWidth - hotSpotDiv.offsetWidth) / 2}px`;
      span.style.marginTop = `${-span.scrollHeight - 12}px`;
    }

    if (args.animated) {
      const div = document.createElement('div');
      const div2 = document.createElement('div');
      hotSpotDiv.appendChild(div);
      hotSpotDiv.appendChild(div2);
    }

    if (args.iconFile) {
      const img = document.createElement('img');
      const baseUrl = `${this.AWS_PATH}/tours/${scene.tourId}/panoramas`;
      img.src = `${baseUrl}/${scene.url}/${args.iconFile}`;
      hotSpotDiv.appendChild(img);
    } else {
      const iconName = hotSpotDiv.dataset.iconName || args.iconName;
      const iconColor = args.iconColor || 'white';
      const IconComponent = getLucideIconByName(iconName);
      const iconElement = createIconElement(IconComponent, iconColor);
      hotSpotDiv.appendChild(iconElement);
    }

    if (args.iconColor) {
      hotSpotDiv.style.color = args.iconColor;
    }
  };

  private zoomIn(pitch: number, yaw: number) {
    if (this.viewer) {
      const bounds = this.viewer.getHfovBounds();
      this.lookAt({
        pitch,
        yaw,
        bounds: bounds[0],
        time: 1000,
      });
    }
  }

  private getMultires(scene: any) {
    let config: any = {};
    if (scene.hd) {
      config = {
        type: scene.type,
        multiRes: {
          basePath: `${this.AWS_PATH}/tours/${scene.tourId}/panoramas/${scene.url}/`,
          path: scene.path,
          fallbackPath: scene.fallbackPath,
          extension: scene.extension,
          tileResolution: scene.tileResolution,
          maxLevel: scene.maxLevel,
          cubeResolution: scene.cubeResolution,
        },
      };
    } else {
      config = {
        panorama: `${this.AWS_PATH}/tours/${scene.tourId}/panoramas/${
          scene.url
        }/full.jpg?v=${moment().format('HHmmss')}`,
      };
    }
    return config;
  }

  public show(showId: string) {
    const toHide = document.getElementById(this.id);
    const toShow = document.getElementById(showId);
    if (toHide && toShow) {
      if (toHide) toHide.style.opacity = '0';
      toShow.classList.add('show');
      toShow.style.opacity = '1';
      setTimeout(() => {
        toHide.classList.remove('show');
      }, 500);
    }
  }

  public loadScene({ scene, pitch, yaw, hfov }: any) {
    if (!this.viewer) throw new Error('No viewer');
    if (pitch && yaw) {
      if (hfov) {
        this.viewer.loadScene(scene, pitch, yaw, hfov);
      } else {
        this.viewer.loadScene(scene, pitch, yaw);
      }
    } else {
      this.viewer.loadScene(scene);
    }
  }

  public lookAt({ pitch, yaw, bounds, time }: any) {
    if (!this.viewer) throw new Error('No viewer');
    if (bounds && time) {
      return this.viewer.lookAt(pitch, yaw, bounds, time);
    }
    return this.viewer.lookAt(pitch, yaw);
  }

  public removeScene(scene: string) {
    if (!this.viewer) throw new Error('No viewer');
    return this.viewer.removeScene(scene);
  }

  public getScene() {
    if (!this.viewer) throw new Error('No viewer');
    return this.viewer.getScene();
  }

  public getYaw() {
    if (!this.viewer) throw new Error('No viewer');
    return this.viewer.getYaw();
  }

  public isLoaded() {
    if (!this.viewer) throw new Error('No viewer');
    return this.viewer.isLoaded();
  }

  public setNorthOffset(offset: number) {
    if (!offset) return null;
    if (!this.viewer) throw new Error('No viewer');
    // the compass of pannellum rotates against the clock. 90deg is 10pm
    return this.viewer.setNorthOffset(offset > 0 ? 360 - offset : offset * -1);
  }

  public setPose(pose: Record<'yaw' | 'hfov' | 'pitch', number>) {
    if (!this.viewer) throw new Error('No viewer');
    if (pose.pitch) {
      this.viewer.setPitch(pose.pitch, true);
    }
    if (pose.yaw) {
      this.viewer.setYaw(pose.yaw, true);
    }
    if (pose.hfov) {
      this.viewer.setHfov(pose.hfov, true);
    }
  }

  public getNorthOffset() {
    if (!this.viewer) throw new Error('No viewer');
    return this.viewer.getNorthOffset();
  }
}
